import React, { useState, useEffect } from 'react';
import { Upload, Button, message, Input, Space, Popconfirm, Modal } from 'antd';
import { UploadOutlined, DeleteOutlined, EyeOutlined, QrcodeOutlined } from '@ant-design/icons';
import { uploadApi, photoTypeApi } from '../../services/api';
import { scanQrCode, isUrl } from '../../utils/qrScan';

const PhotoUpload = ({
  productId,
  batchId,
  isBatchUpload = false,
  photoType = 1,
  rawMaterialPhotos = [],
  productionPhotos = [],
  finishedPhotos = [],
  certificatePhotos = [],
  cuttingPhotos = [],
  planningPhotos = [],
  handworkPhotos = [],
  polishingPhotos = [],
  pillarDrillingPhotos = [],
  beadGrindingPhotos = [],
  drillingPhotos = [],
  photosByType = {}, // 动态按类型分组的照片
  productPhotoTypes = null, // 货品级照片类型列表 [{id, name, ...}]
  onUploadSuccess,
  deletePhotoApi, // 可选的自定义删除照片API
  uploadPhotoApi // 可选的自定义上传照片API
}) => {
  const [uploading, setUploading] = useState(false);
  const [descriptions, setDescriptions] = useState({});
  const [fileList, setFileList] = useState([]);
  const [activeType, setActiveType] = useState(photoType);
  const [loadedPhotoTypes, setLoadedPhotoTypes] = useState([]);

  // 如果没传productPhotoTypes（undefined/null），从API加载货品级照片类型
  // 如果传了空数组[]，说明品类没有货品级照片，不加载
  useEffect(() => {
    if (!isBatchUpload && productPhotoTypes === undefined) {
      photoTypeApi.all().then(res => {
        const productTypes = (res.data || []).filter(t => t.level === 'product' && t.status !== 0);
        productTypes.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        setLoadedPhotoTypes(productTypes);
        if (productTypes.length > 0) {
          setActiveType(productTypes[0].id);
        }
      }).catch(() => {});
    }
  }, [isBatchUpload, productPhotoTypes]);

  const activePhotoTypes = productPhotoTypes?.length > 0 ? productPhotoTypes : loadedPhotoTypes;

  // 确保activeType在activePhotoTypes范围内
  useEffect(() => {
    if (activePhotoTypes.length > 0 && !activePhotoTypes.find(t => t.id === activeType)) {
      setActiveType(activePhotoTypes[0].id);
    }
  }, [activePhotoTypes, activeType]);

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('请选择要上传的文件');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    fileList.forEach(file => {
      formData.append('photos', file);
    });

    // 照片描述
    const descList = fileList.map((_, i) => descriptions[i] || '');
    formData.append('descriptions', JSON.stringify(descList));

    formData.append('photoType', isBatchUpload ? photoType : activeType);

    try {
      if (uploadPhotoApi) {
        await uploadPhotoApi(formData);
      } else if (isBatchUpload) {
        await uploadApi.uploadBatchPhotos(batchId, formData);
      } else {
        await uploadApi.uploadProductPhotos(productId, formData);
      }
      message.success('上传成功');
      setFileList([]);
      setDescriptions({});
      onUploadSuccess?.();
    } catch (err) {
      message.error('上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    try {
      if (deletePhotoApi) {
        await deletePhotoApi(photoId);
      } else {
        await uploadApi.deletePhoto(photoId);
      }
      message.success('删除成功');
      onUploadSuccess?.();
    } catch {
      message.error('删除失败');
    }
  };

  const handleScanQr = async (photo) => {
    const hide = message.loading('扫描二维码中...', 0);
    try {
      const result = await scanQrCode(photo.id);
      hide();
      if (result && isUrl(result)) {
        Modal.info({
          title: '检测到二维码链接',
          content: (
            <div>
              <p style={{ wordBreak: 'break-all', marginBottom: 12 }}>{result}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button type="primary" size="small" onClick={() => window.open(result, '_blank')}>打开链接</Button>
                <Button size="small" onClick={() => { navigator.clipboard.writeText(result); message.success('已复制'); }}>复制链接</Button>
              </div>
            </div>
          ),
          okText: '关闭',
        });
      } else if (result) {
        Modal.info({
          title: '检测到二维码内容',
          content: (
            <div>
              <p style={{ wordBreak: 'break-all', marginBottom: 12 }}>{result}</p>
              <Button size="small" onClick={() => { navigator.clipboard.writeText(result); message.success('已复制'); }}>复制内容</Button>
            </div>
          ),
          okText: '关闭',
        });
      } else {
        message.warning('该证书照片中未检测到二维码');
      }
    } catch {
      hide();
      message.error('扫描失败');
    }
  };

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);

      // 同步删除描述
      const newDescriptions = { ...descriptions };
      delete newDescriptions[index];
      setDescriptions(newDescriptions);
    },
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('只能上传图片文件!');
        return Upload.LIST_IGNORE;
      }
      setFileList((prev) => [...prev, file]);
      return false;
    },
    fileList,
    multiple: true,
    listType: 'picture-card',
  };

  const renderPhotoList = (photos, type, typeName) => (
    <div style={{ marginTop: 16 }}>
      <h4 style={{ marginBottom: 12, color: '#666' }}>
        {typeName || `类型${type}`} ({photos.length}张)
      </h4>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {photos.map(photo => (
          <div key={photo.id} style={{
            position: 'relative',
            width: 100,
            height: 100,
            borderRadius: 8,
            overflow: 'hidden',
            border: '1px solid #f0f0f0'
          }}>
            <img
              src={photo.url || photo.thumbnail_url}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              padding: '4px 8px',
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 2
            }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <a
                  href={photo.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#fff', fontSize: 12 }}
                >
                  <EyeOutlined />
                </a>
                {type === 4 && (
                  <span
                    onClick={() => handleScanQr(photo)}
                    style={{ color: '#fff', fontSize: 12, cursor: 'pointer' }}
                    title="扫描二维码"
                  >
                    <QrcodeOutlined />
                  </span>
                )}
              </div>
              <Popconfirm
                title="删除这张照片?"
                onConfirm={() => handleDelete(photo.id)}
              >
                <span style={{ color: '#ff4d4f', fontSize: 12, cursor: 'pointer' }}>
                  <DeleteOutlined />
                </span>
              </Popconfirm>
            </div>
            {photo.description && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                padding: '2px 4px',
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                fontSize: 10,
                textAlign: 'center',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                pointerEvents: 'none',
                zIndex: 1
              }}>
                {photo.description}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // 获取照片类型名称
  const getTypeName = (typeId) => {
    const found = activePhotoTypes?.find(t => t.id === typeId);
    return found?.name || `类型${typeId}`;
  };

  // 获取某个类型的已有照片
  const getPhotosForType = (typeId) => {
    // 优先使用photosByType
    if (photosByType && Object.keys(photosByType).length > 0) {
      return photosByType[String(typeId)] || [];
    }
    // 兼容旧字段
    switch (typeId) {
      case 1: return rawMaterialPhotos;
      case 2: return productionPhotos;
      case 3: return finishedPhotos;
      case 4: return certificatePhotos;
      case 5: return cuttingPhotos;
      case 6: return planningPhotos;
      case 7: return handworkPhotos;
      case 8: return polishingPhotos;
      case 11: return pillarDrillingPhotos;
      case 12: return beadGrindingPhotos;
      case 13: return drillingPhotos;
      default: return [];
    }
  };

  return (
    <div>
      {/* 照片类型选择 (仅货品上传) */}
      {!isBatchUpload && activePhotoTypes && activePhotoTypes.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <span style={{ marginRight: 12 }}>照片类型:</span>
          <Space wrap>
            {activePhotoTypes.map(pt => (
              <Button
                key={pt.id}
                type={activeType === pt.id ? 'primary' : 'default'}
                size="small"
                onClick={() => setActiveType(pt.id)}
              >
                {pt.name}
              </Button>
            ))}
          </Space>
        </div>
      )}

      {/* 上传区域 */}
      <div style={{ marginBottom: 16 }}>
        <Upload {...uploadProps}>
          {fileList.length < 20 && (
            <div>
              <UploadOutlined />
              <div style={{ marginTop: 8 }}>选择图片</div>
            </div>
          )}
        </Upload>

        {fileList.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <p style={{ marginBottom: 8 }}>添加描述 (可选):</p>
            {fileList.map((file, index) => (
              <div key={file.uid} style={{ marginBottom: 8 }}>
                <Input
                  size="small"
                  placeholder={`图片 ${index + 1} 的描述`}
                  value={descriptions[index] || ''}
                  onChange={(e) => setDescriptions({
                    ...descriptions,
                    [index]: e.target.value
                  })}
                  style={{ width: 200 }}
                />
              </div>
            ))}
            <Button
              type="primary"
              onClick={handleUpload}
              loading={uploading}
              style={{ marginTop: 8, background: '#8B7355' }}
            >
              开始上传 ({fileList.length}张)
            </Button>
          </div>
        )}
      </div>

      {/* 已有照片列表 */}
      {isBatchUpload ? (
        <>
          {rawMaterialPhotos.length > 0 && renderPhotoList(rawMaterialPhotos, 1, '原石照片')}
          {productionPhotos.length > 0 && renderPhotoList(productionPhotos, 2, '生产过程')}
          {finishedPhotos.length > 0 && renderPhotoList(finishedPhotos, 3, '成品照片')}
          {cuttingPhotos.length > 0 && renderPhotoList(cuttingPhotos, 5, '切料')}
          {planningPhotos.length > 0 && renderPhotoList(planningPhotos, 6, '规划')}
          {handworkPhotos.length > 0 && renderPhotoList(handworkPhotos, 7, '手修')}
          {polishingPhotos.length > 0 && renderPhotoList(polishingPhotos, 8, '抛光')}
          {pillarDrillingPhotos.length > 0 && renderPhotoList(pillarDrillingPhotos, 11, '打柱')}
          {beadGrindingPhotos.length > 0 && renderPhotoList(beadGrindingPhotos, 12, '磨珠')}
          {drillingPhotos.length > 0 && renderPhotoList(drillingPhotos, 13, '穿珠子')}
        </>
      ) : (
        <>
          {activePhotoTypes && activePhotoTypes.length > 0 ? (
            // 动态渲染所有货品级照片类型
            activePhotoTypes.map(pt => {
              const photos = getPhotosForType(pt.id);
              return photos.length > 0 ? (
                <div key={pt.id}>
                  {renderPhotoList(photos, pt.id, pt.name)}
                </div>
              ) : null;
            })
          ) : (
            // 品类未关联任何货品级照片类型时不显示
            null
          )}
        </>
      )}
    </div>
  );
};

export default PhotoUpload;
